import React, { useMemo } from 'react';
import styled from 'styled-components';
import { BodyText } from '@kvdbil/components';
import { ProcessedAuctionStates } from '~/helpers/auction';
import { inTimeZone } from '~/helpers/timezone';
import { useDisplayTimeZone } from '~/App/shared/hooks/useDisplayTimeZone';
import { useTranslation } from '~/Locale';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import AuctionCardTimer from '~/App/shared/components/ProductCard/components/ContainerWithImage/components/AuctionCardTimer';
import { AuctionType } from '~/App/shared/interfaces/Auction';

const Container = styled.div<{ reverse?: boolean }>`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
`;

const TimerContainer = styled.div`
  display: flex;
  margin-left: 4px;
`;

const EndingTimerValue = styled(BodyText)`
  font-weight: bold;
`;

export interface AuctionStatsV2Props {
  activeAuction: ProcessedAuction['activeAuction'];
  auctionStates: ProcessedAuctionStates;
  buyMethod: AuctionType;
  closeAt: string;
}

const EndingTimer = ({
  activeAuction,
  auctionStates,
  buyMethod,
  closeAt
}: AuctionStatsV2Props) => {
  const { t } = useTranslation();
  const displayTimeZone = useDisplayTimeZone();
  const { isClosed, isBuyNowAvailable } = auctionStates;
  const value = inTimeZone(closeAt, displayTimeZone).format('D MMM, HH:mm');
  const isOnlyFixedPrice = buyMethod === AuctionType.BUY_NOW;
  const isTimerActive = Boolean(
    !isOnlyFixedPrice && auctionStates.isCounting && activeAuction
  );
  const closingDescription = useMemo(() => {
    if (isOnlyFixedPrice) {
      return isBuyNowAvailable
        ? t('Fixed price closes at')
        : t('Fixed price closed at');
    }
    return isClosed ? t('Bidding closed at') : t('Bidding closes at');
  }, [isOnlyFixedPrice, isBuyNowAvailable, isClosed, t]);

  return (
    <Container>
      <BodyText>{`${closingDescription}: `}</BodyText>
      <TimerContainer>
        {isTimerActive ? (
          <AuctionCardTimer closeAt={new Date(closeAt)} />
        ) : (
          <EndingTimerValue>{value}</EndingTimerValue>
        )}
      </TimerContainer>
    </Container>
  );
};

export default EndingTimer;
